/**
 * MEGA MENU TRANSITION CONTROLLER
 * Par Breaker Golf Tech
 *
 * Replaces the theme's basic mouseover/mouseleave with a comprehensive
 * hover-intent system that handles all transition scenarios:
 *
 * 1. OPEN:      60ms hover-intent delay before revealing dropdown
 * 2. CLOSE:     150ms grace period before hiding (forgives sloppy mouse paths)
 * 3. CROSSFADE: Instantly close old, open new — no gap, no flicker
 * 4. RAPID:     Hover-intent cancels if cursor leaves within 60ms
 * 5. RETURN:    Re-entering cancels the close timer, dropdown stays open
 * 6. KEYBOARD:  Respects native <details> for keyboard/screen reader users
 */
(function () {
  'use strict';

  var OPEN_DELAY = 60; // ms to wait before opening (hover intent)
  var CLOSE_DELAY = 150; // ms grace period before closing
  var ENTER_FRAME_DELAY = 8; // ms for entering class before active (allows CSS transition)
  var LEAVE_DURATION = 180; // ms — matches CSS .megamenu--leaving transition

  var activeDetail = null; // currently open <details> element
  var openTimer = null; // hover-intent open timeout
  var closeTimer = null; // close grace timeout
  var leaveTimer = null; // CSS leave animation timeout
  var openSequence = 0; // invalidates delayed enter callbacks from previous menus
  var pinnedDetail = null; // click-opened menu that should stay open until outside click
  var pinnedMode = false; // once enabled, hovering between labels keeps menus pinned
  var pendingLinkNavigation = false;
  var initialized = false;

  function init() {
    if (initialized) return;

    var headerEl = document.querySelector('[data-section-type="header"]');
    if (!headerEl) return;

    var details = headerEl.querySelectorAll('details[data-hover="true"]');
    if (!details.length) return;

    // Remove the theme's existing mouseover/mouseleave by cloning the details elements
    // Actually — we can't easily remove them. Instead, we'll intercept by setting a flag.
    // Better approach: override by attaching our own listeners with capture phase
    // and stopping propagation so the theme's don't fire.

    details.forEach(function (detail) {
      var summary = detail.querySelector('summary');
      var dropdown = detail.querySelector('.megamenu--bluetees');
      if (!dropdown) return;

      // Intercept theme's built-in handlers using capture phase
      // stopImmediatePropagation prevents theme.js bubble-phase handlers from firing
      detail.addEventListener(
        'mouseover',
        function (e) {
          e.stopImmediatePropagation();
          handleMouseEnter(detail, dropdown);
        },
        true
      );

      detail.addEventListener(
        'mouseleave',
        function (e) {
          e.stopImmediatePropagation();
          handleMouseLeave(detail, dropdown);
        },
        true
      );

      // Intercept focus events too (theme.js also has focusin/focusout handlers)
      detail.addEventListener(
        'focusin',
        function (e) {
          e.stopImmediatePropagation();
          cancelAllTimers();
          openDropdown(detail, dropdown);
        },
        true
      );

      detail.addEventListener(
        'focusout',
        function (e) {
          e.stopImmediatePropagation();
          // Small timeout to allow focus to move to child elements
          setTimeout(function () {
            if (!detail.matches(':focus-within')) {
              closeDropdown(detail, dropdown);
            }
          }, 10);
        },
        true
      );

      // Summary click: act as a menu control. The destination page lives
      // as an explicit link inside the dropdown so touch users do not miss it.
      summary.addEventListener(
        'click',
        function (e) {
          if (e.target.closest && e.target.closest('a[href]')) return;

          e.preventDefault();
          e.stopImmediatePropagation();
          cancelAllTimers();

          if (activeDetail && activeDetail !== detail) {
            var oldDropdown = activeDetail.querySelector('.megamenu--bluetees');
            if (oldDropdown) {
              instantClose(activeDetail, oldDropdown);
            }
          }

          openDropdown(detail, dropdown);
          pinnedMode = true;
          pinnedDetail = detail;
        },
        true
      );

    });

    // Close if mouse leaves header entirely
    headerEl.addEventListener('mouseleave', function () {
      if (pinnedMode) return;

      if (activeDetail) {
        var dropdown = activeDetail.querySelector('.megamenu--bluetees');
        if (dropdown) {
          scheduleClose(activeDetail, dropdown);
        }
      } else {
        // Safety net: ensure backdrop + pill corners are restored
        document.body.classList.remove('megamenu-overlay-active');
        var pw = document.querySelector('#SiteHeader > .page-width');
        if (pw) pw.classList.remove('megamenu-pill-open');
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && activeDetail) {
        var dropdown = activeDetail.querySelector('.megamenu--bluetees');
        if (dropdown) {
          pinnedMode = false;
          pinnedDetail = null;
          closeDropdown(activeDetail, dropdown);
          var summary = activeDetail.querySelector('summary');
          if (summary) summary.focus();
        }
      }
    });

    document.addEventListener('pointerdown', handleDropdownLinkPointerDown, true);
    document.addEventListener('mousedown', handleDropdownLinkPointerDown, true);

    document.addEventListener(
      'click',
      function (e) {
        if (!e.target.closest) return;

        var link = e.target.closest('.megamenu--bluetees a[href]');
        if (!link) return;

        handleDropdownLinkClick(e, link);
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        if (!pinnedMode || !pinnedDetail || pinnedDetail.contains(e.target)) return;

        var dropdown = pinnedDetail.querySelector('.megamenu--bluetees');
        if (dropdown) {
          closeDropdown(pinnedDetail, dropdown);
        }
        pinnedMode = false;
        pinnedDetail = null;
      },
      true
    );

    initialized = true;
  }

  function handleDropdownLinkPointerDown(e) {
    if (!pinnedMode || !e.target.closest) return;

    var link = e.target.closest('.megamenu--bluetees a[href]');
    if (pendingLinkNavigation || !link || !isNavigableLink(link) || isModifiedClick(e)) return;

    if (isAppsMenuLink(link)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      navigateAppsLink(link.href);
      return;
    }

    var url = link.href;
    pendingLinkNavigation = true;
    window.setTimeout(function () {
      window.location.assign(url);
    }, 0);
  }

  function handleDropdownLinkClick(e, link) {
    if (isModifiedClick(e) || link.target === '_blank' || link.hasAttribute('download')) {
      return;
    }

    if (isAppsMenuLink(link)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      navigateAppsLink(link.href);
      return;
    }

    if (!isNavigableLink(link)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    pinnedMode = false;
    pinnedDetail = null;
    pendingLinkNavigation = true;

    window.location.href = link.href;
  }

  function navigateAppsLink(url) {
    pinnedMode = false;
    pinnedDetail = null;
    pendingLinkNavigation = true;
    window.location.assign(url);
  }

  function isModifiedClick(e) {
    return e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
  }

  function isNavigableLink(link) {
    var href = link.getAttribute('href');
    return !!href && href.charAt(0) !== '#' && link.target !== '_blank' && !link.hasAttribute('download');
  }

  function isAppsMenuLink(link) {
    return !!link.closest('.megamenu--apps');
  }

  function handleMouseEnter(detail, dropdown) {
    // Cancel any pending close (RETURN scenario)
    clearTimeout(closeTimer);
    closeTimer = null;

    // Cancel any pending leave animation
    clearTimeout(leaveTimer);
    leaveTimer = null;

    // If this detail is already active, just ensure it stays active
    if (activeDetail === detail && dropdown.classList.contains('megamenu--active')) {
      return;
    }

    // If another menu is currently open (CROSSFADE scenario)
    if (activeDetail && activeDetail !== detail) {
      var oldDropdown = activeDetail.querySelector('.megamenu--bluetees');
      if (oldDropdown) {
        // Instantly hide the old one (no leave animation for crossfade — keeps it snappy)
        instantClose(activeDetail, oldDropdown);
      }
    }

    // Clear any existing open timer (RAPID scenario)
    clearTimeout(openTimer);

    // Hover-intent: wait before opening
    openTimer = setTimeout(function () {
      openTimer = null;
      openDropdown(detail, dropdown);
      if (pinnedMode) {
        pinnedDetail = detail;
      }
    }, OPEN_DELAY);
  }

  function handleMouseLeave(detail, dropdown) {
    if (pinnedMode) {
      if (detail !== pinnedDetail) {
        clearTimeout(openTimer);
        openTimer = null;
      }
      return;
    }

    // Cancel any pending open (RAPID scenario — cursor left before intent delay)
    clearTimeout(openTimer);
    openTimer = null;

    // If this dropdown is active, schedule a close with grace period
    if (activeDetail === detail) {
      scheduleClose(detail, dropdown);
    }
  }

  function scheduleClose(detail, dropdown) {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      closeTimer = null;
      closeDropdown(detail, dropdown);
    }, CLOSE_DELAY);
  }

  function openDropdown(detail, dropdown) {
    closeSiblingDropdowns(detail);
    cancelEnter(dropdown);

    // Remove leaving state if it was in the process of closing
    dropdown.classList.remove('megamenu--leaving');

    // Set <details> open for accessibility
    if (!detail.hasAttribute('open')) {
      detail.setAttribute('open', '');
    }
    var summary = detail.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'true');
    }

    // Mark the nav item as active (for underline)
    var parentLi = detail.closest('.site-nav__item');
    if (parentLi) {
      // Remove active from all nav items first
      document.querySelectorAll('.site-nav__item.megamenu--item-active').forEach(function (li) {
        li.classList.remove('megamenu--item-active');
      });
      parentLi.classList.add('megamenu--item-active');
    }

    // Two-frame open: entering → active (allows CSS transition to fire)
    dropdown.classList.add('megamenu--entering');
    dropdown.classList.remove('megamenu--active');

    // Backdrop blur overlay + flatten pill bottom corners
    document.body.classList.add('megamenu-overlay-active');
    var pageWidth = document.querySelector('#SiteHeader > .page-width');
    if (pageWidth) pageWidth.classList.add('megamenu-pill-open');

    var openToken = ++openSequence;
    dropdown._pbOpenToken = openToken;

    requestAnimationFrame(function () {
      if (dropdown._pbOpenToken !== openToken || !detail.hasAttribute('open')) return;

      dropdown._pbEnterTimer = setTimeout(function () {
        dropdown._pbEnterTimer = null;
        if (dropdown._pbOpenToken !== openToken || !detail.hasAttribute('open')) return;

        dropdown.classList.remove('megamenu--entering');
        dropdown.classList.add('megamenu--active');
      }, ENTER_FRAME_DELAY);
    });

    activeDetail = detail;
  }

  function closeDropdown(detail, dropdown) {
    cancelEnter(dropdown);

    if (pinnedDetail === detail) {
      pinnedMode = false;
      pinnedDetail = null;
    }

    if (
      !dropdown.classList.contains('megamenu--active') &&
      !dropdown.classList.contains('megamenu--entering')
    ) {
      // Already closed
      cleanup(detail, dropdown);
      return;
    }

    // Start leave animation
    dropdown.classList.remove('megamenu--active');
    dropdown.classList.remove('megamenu--entering');
    dropdown.classList.add('megamenu--leaving');

    // Remove nav item active state
    var parentLi = detail.closest('.site-nav__item');
    if (parentLi) {
      parentLi.classList.remove('megamenu--item-active');
    }

    if (activeDetail === detail) {
      activeDetail = null;
    }

    // After leave animation completes, clean up
    clearTimeout(leaveTimer);
    leaveTimer = setTimeout(function () {
      leaveTimer = null;
      dropdown.classList.remove('megamenu--leaving');
      cleanup(detail, dropdown);
      // Remove backdrop blur if no menu is open
      if (!activeDetail) {
        document.body.classList.remove('megamenu-overlay-active');
      }
    }, LEAVE_DURATION);
  }

  function instantClose(detail, dropdown) {
    cancelEnter(dropdown);

    if (!pinnedMode && pinnedDetail === detail) {
      pinnedDetail = null;
    }

    // Immediate close without animation (used for crossfade)
    dropdown.classList.remove('megamenu--active');
    dropdown.classList.remove('megamenu--entering');
    dropdown.classList.remove('megamenu--leaving');
    cleanup(detail, dropdown);

    // Remove nav item active state
    var parentLi = detail.closest('.site-nav__item');
    if (parentLi) {
      parentLi.classList.remove('megamenu--item-active');
    }

    if (activeDetail === detail) {
      activeDetail = null;
    }
  }

  function cleanup(detail, dropdown) {
    cancelEnter(dropdown);

    // Remove <details> open attribute (hides from accessibility tree)
    if (detail.hasAttribute('open')) {
      detail.removeAttribute('open');
    }
    var summary = detail.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
    }
    // Remove backdrop blur + restore pill corners if nothing is open
    if (!activeDetail) {
      document.body.classList.remove('megamenu-overlay-active');
      var pageWidth = document.querySelector('#SiteHeader > .page-width');
      if (pageWidth) pageWidth.classList.remove('megamenu-pill-open');
    }
  }

  function cancelAllTimers() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    clearTimeout(leaveTimer);
    openTimer = null;
    closeTimer = null;
    leaveTimer = null;
  }

  function cancelEnter(dropdown) {
    if (!dropdown) return;

    if (dropdown._pbEnterTimer) {
      clearTimeout(dropdown._pbEnterTimer);
      dropdown._pbEnterTimer = null;
    }

    dropdown._pbOpenToken = null;
  }

  function closeSiblingDropdowns(currentDetail) {
    var headerEl = document.querySelector('[data-section-type="header"]');
    if (!headerEl) return;

    headerEl.querySelectorAll('details[data-hover="true"]').forEach(function (otherDetail) {
      if (otherDetail === currentDetail) return;

      var otherDropdown = otherDetail.querySelector('.megamenu--bluetees');
      if (otherDropdown) {
        cancelEnter(otherDropdown);
        otherDropdown.classList.remove('megamenu--active', 'megamenu--entering', 'megamenu--leaving');
      }

      otherDetail.removeAttribute('open');
      otherDetail.setAttribute('aria-expanded', 'false');

      var otherSummary = otherDetail.querySelector('summary');
      if (otherSummary) {
        otherSummary.setAttribute('aria-expanded', 'false');
      }

      var otherItem = otherDetail.closest('.site-nav__item');
      if (otherItem) {
        otherItem.classList.remove('megamenu--item-active');
      }
    });

    if (activeDetail && activeDetail !== currentDetail) {
      activeDetail = null;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM is already ready — but wait a tick to ensure theme.js has initialized first
    // so our capture-phase listeners fire before the theme's
    setTimeout(init, 0);
  }
})();
